import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Lock, ShieldCheck } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-5 text-gray-900">
      <div className="absolute top-0 left-0 right-0 h-1 austria-stripe" />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex w-14 h-14 rounded-2xl items-center justify-center text-white mb-4" style={{ background: "var(--austria-red)" }}>
            <Lock size={26} />
          </div>
          <h1 className="font-heading font-black text-2xl text-gray-900">Austria Tips</h1>
          <p className="text-sm font-body text-gray-500 mt-1">{t("admin.loginTitle")}</p>
        </div>

        <form onSubmit={submit} className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm space-y-4" data-testid="admin-login-form">
          <div>
            <label className="block text-xs font-bold font-body uppercase tracking-wide text-gray-500 mb-1.5">{t("admin.username")}</label>
            <input
              data-testid="admin-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-[var(--austria-red)] transition-colors"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="block text-xs font-bold font-body uppercase tracking-wide text-gray-500 mb-1.5">{t("admin.password")}</label>
            <input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-[var(--austria-red)] transition-colors"
              autoComplete="current-password"
            />
          </div>
          {error && <div data-testid="admin-login-error" className="text-sm font-body text-[var(--austria-red)] bg-red-50 px-3 py-2 rounded-lg">{error}</div>}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-white font-bold font-body text-sm disabled:opacity-60"
            style={{ background: "var(--austria-red)" }}
          >
            {loading ? <Loader2 size={17} className="animate-spin" /> : <ShieldCheck size={17} />}
            {t("admin.login")}
          </button>
        </form>
      </div>
    </div>
  );
}
